import { CreateTxOptions } from '@terra-money/terra.js'
import { useConnectedWallet } from '@terra-money/wallet-provider'
import { Asset, AssetInfo } from 'interfaces/contracts/edgePool'
import { useCallback } from 'react'
import { assetService } from 'services/assetService'
import { poolService } from 'services/poolService'
import { extractAddrOrDenomFromInfo } from 'utils/contractParse'
import useBroadcast from './useBroadcast'

const usePoolTx = () => {
  const connectedWallet = useConnectedWallet()

  const { send: _sendLock } = useBroadcast()
  const sendLock = useCallback(
    (assetInfo: AssetInfo, options?: CreateTxOptions) => {
      if (!connectedWallet) return

      const { symbol } =
        assetService.assets[extractAddrOrDenomFromInfo(assetInfo)]

      const txs = { ...poolService.lock(assetInfo), ...options }
      _sendLock(txs, `Lock ${symbol} as a collateral`)
    },
    [_sendLock, connectedWallet],
  )

  const { send: _sendUnlock } = useBroadcast()
  const sendUnlock = useCallback(
    (assetInfo: AssetInfo, options?: CreateTxOptions) => {
      if (!connectedWallet) return

      const { symbol } =
        assetService.assets[extractAddrOrDenomFromInfo(assetInfo)]

      const txs = { ...poolService.unlock(assetInfo), ...options }
      _sendUnlock(txs, `Lock ${symbol} as a collateral`)
    },
    [_sendUnlock, connectedWallet],
  )

  const { send: _sendSupply } = useBroadcast()
  const sendSupply = useCallback(
    (
      asset: Asset,
      isLock: boolean,
      sendLock: boolean,
      options?: CreateTxOptions,
    ) => {
      if (!connectedWallet) return

      const { symbol } =
        assetService.assets[extractAddrOrDenomFromInfo(asset.info)]

      let txs: CreateTxOptions
      if (sendLock) {
        txs = {
          ...poolService.depositAndLockFunction(asset, isLock),
          ...options,
        }
        const action = isLock ? 'Lock' : 'Unlock'
        _sendSupply(
          txs,
          `Supply ${asset.amount} ${symbol} and ${action} ${symbol}`,
        )
      } else {
        txs = { ...poolService.deposit(asset), ...options }
        _sendSupply(txs, `Supply ${asset.amount} ${symbol}`)
      }
    },
    [_sendSupply, connectedWallet],
  )

  const { send: _sendWithdraw } = useBroadcast()
  const sendWithdraw = useCallback(
    (asset: Asset, options?: CreateTxOptions) => {
      if (!connectedWallet) return

      let txs = { ...poolService.withdraw(asset), ...options }

      _sendWithdraw(
        txs,
        `Withdraw ${asset.amount} ${extractAddrOrDenomFromInfo(asset.info)}`,
      )
    },
    [_sendWithdraw, connectedWallet],
  )

  const { send: _sendBorrow } = useBroadcast()
  const sendBorrow = useCallback(
    (asset: Asset, options?: CreateTxOptions) => {
      if (!connectedWallet) return

      const { symbol } =
        assetService.assets[extractAddrOrDenomFromInfo(asset.info)]

      const txs = { ...poolService.borrow(asset), ...options }
      _sendBorrow(txs, `Borrow ${asset.amount} ${symbol}`)
    },
    [_sendBorrow, connectedWallet],
  )

  const { send: _sendRepay } = useBroadcast()
  const sendRepay = useCallback(
    (asset: Asset, options?: CreateTxOptions) => {
      if (!connectedWallet) return

      const { symbol } =
        assetService.assets[extractAddrOrDenomFromInfo(asset.info)]

      const txs = { ...poolService.repay(asset), ...options }
      _sendRepay(txs, `Repay ${asset.amount} ${symbol}`)
    },
    [_sendRepay, connectedWallet],
  )

  return {
    sendLock,
    sendUnlock,
    sendSupply,
    sendWithdraw,
    sendBorrow,
    sendRepay,
  }
}

export default usePoolTx
